// ./src/components/generic/VolumeUnitSelector/VolumeUnitSelector.tsx
import React, { useCallback, useMemo } from "react";
import {
  SegmentedControl,
  SegmentedControlProps,
  Select,
  Input,
  ComboboxProps,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VOLUME_UNIT_OPTIONS, VOLUME_UNIT_MAP } from "@/config";

// ============================================
// Types
// ============================================

export interface VolumeUnitSelectorProps
  extends Omit<SegmentedControlProps, "data" | "value" | "onChange"> {
  /** Field name for form handling */
  name?: string;

  /** Selected volume unit ('l', 'gal-us', 'gal-uk') */
  value?: string | null;

  /** Callback when unit changes */
  onChange?: (name: string | undefined, value: string | null) => void;

  /** Label text */
  label?: string;

  /** Description text below the label */
  description?: string;

  /** Whether the field is required (shows asterisk) */
  required?: boolean;

  /** Whether the control is disabled */
  disabled?: boolean;

  /** Error message to display */
  error?: string;

  /** Size of the control */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Use full width */
  fullWidth?: boolean;

  /**
   * Compact mode - uses a Select dropdown instead of SegmentedControl.
   * Useful when placed next to input fields where space is limited.
   */
  compact?: boolean;

  /**
   * Width for compact mode Select. Default is 130.
   */
  compactWidth?: string | number;

  /** Props passed to the underlying Combobox in compact mode */
  comboboxProps?: ComboboxProps;

  /** Render dropdown in portal (for compact mode inside drawers/modals) */
  withinPortal?: boolean;

  /** Left section icon (ignored for SegmentedControl, used in compact mode) */
  leftSection?: React.ReactNode;

  /** Placeholder for compact mode Select */
  placeholder?: string;
}

// ============================================
// Component
// ============================================

export const VolumeUnitSelector: React.FC<VolumeUnitSelectorProps> = ({
  name,
  value,
  onChange,
  label,
  description,
  required = false,
  disabled = false,
  error,
  size = "md",
  fullWidth = false,
  compact = false,
  compactWidth = '100%',
  comboboxProps,
  withinPortal,
  leftSection,
  placeholder,
  ...rest
}) => {
  const { t } = useTranslation(["common"]);

  // Handle change for SegmentedControl
  const handleSegmentedChange = useCallback(
    (newValue: string) => {
      onChange?.(name, newValue);
    },
    [name, onChange]
  );

  // Handle change for Select
  const handleSelectChange = useCallback(
    (newValue: string | null) => {
      onChange?.(name, newValue);
    },
    [name, onChange]
  );

  // Options for SegmentedControl
  const segmentedOptions = useMemo(() => {
    return VOLUME_UNIT_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(`common:${opt.labelKey}`),
    }));
  }, [t]);

  // Options for Select (full labels)
  const selectOptions = useMemo(() => {
    return VOLUME_UNIT_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(`common:${opt.labelKey}`), // Liters, US Gallons, UK Gallons
    }));
  }, [t]);

  // Get current value label for aria
  const currentLabel = useMemo(() => {
    if (!value) return "";
    const unit = VOLUME_UNIT_MAP[value];
    return unit ? t(`common:${unit.labelKey}`) : "";
  }, [value, t]);

  // Default to liters if no value
  const currentValue = value || "l";

  // Build combobox props for Select
  const finalComboboxProps = useMemo((): ComboboxProps | undefined => {
    if (!compact) return undefined;

    const baseProps: ComboboxProps = {
      ...(comboboxProps || {}),
    };

    if (withinPortal) {
      baseProps.withinPortal = true;
      baseProps.zIndex = 1000;
    }

    if (Object.keys(baseProps).length === 0) {
      return undefined;
    }

    return baseProps;
  }, [compact, withinPortal, comboboxProps]);

  // Compact mode: render Select dropdown
  if (compact) {
    return (
      <Select
        data={selectOptions}
        value={currentValue}
        onChange={handleSelectChange}
        label={label}
        description={description}
        placeholder={placeholder || t("common:selectors.selectUnit")}
        error={error}
        required={required}
        disabled={disabled}
        size={size}
        w={compactWidth}
        allowDeselect={false}
        comboboxProps={finalComboboxProps}
        leftSection={leftSection}
        withAsterisk={required}
      />
    );
  }

  // Regular mode: render SegmentedControl
  return (
    <Input.Wrapper
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
    >
      <SegmentedControl
        data={segmentedOptions}
        value={currentValue}
        onChange={handleSegmentedChange}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        aria-label={currentLabel}
        mt={label || description ? 4 : 0}
        {...rest}
      />
    </Input.Wrapper>
  );
};